import { SimpleMessageService, Tenant, WebAppCommonModule } from "@allbesmart/web-app-common";
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag } from "@angular/cdk/drag-drop";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Subscription } from "rxjs";
import { first } from "rxjs/operators";
import { ORDER_TYPE_CONFIG } from "src/app/configs/order-type-config";
import { OrderType } from "src/app/enums/order-type";
import {
  BillOfMaterialsItem,
  OrderDto,
  PutOrderDto,
} from "src/app/models/order-dto";
import { OrderService } from "src/app/services/order.service";
import { TenantManagementService } from "src/app/services/tenant-management.service";
import { downloadBillOfMaterialsCsv } from "../../../helpers/bill-of-materials-csv";
import { TenantInfoComponent } from "../tenant-info/tenant-info.component";
import { UpperCasePipe, DatePipe } from "@angular/common";

@Component({
    selector: "oaibox-operation-item-modal",
    templateUrl: "./operation-item-modal.component.html",
    styleUrl: "./operation-item-modal.component.css",
    imports: [
        WebAppCommonModule,
        CdkDropList,
        CdkDrag,
        TenantInfoComponent,
        ReactiveFormsModule,
        UpperCasePipe,
        DatePipe,
    ],
})
export class OperationItemModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) order!: OrderDto;

  protected readonly OrderType = OrderType;
  protected readonly ORDER_TYPE_CONFIG = ORDER_TYPE_CONFIG;

  protected dueDateIsOverdue: boolean = false;
  protected orderProgress?: number;
  protected bom: BillOfMaterialsItem[] = [];
  protected tenants: Tenant[] = [];

  protected createBomItemForm: CreateBomItemFormGroup;

  protected updateBomItemForm: UpdateBomItemFormGroup;
  private selectedBomItemName?: string;

  private tenantsSubscription?: Subscription;
  private updateBomSubscription?: Subscription;

  constructor(
    private readonly tenantManagementService: TenantManagementService,
    private readonly orderService: OrderService,
    private readonly simpleMessageService: SimpleMessageService
  ) {
    this.createBomItemForm = this.getNewCreateBomItemForm();

    this.updateBomItemForm = new FormGroup({
      name: new FormControl<string | null>(null),
      quantity: new FormControl<number | null>(null),
      isAvailable: new FormControl<boolean | null>(null),
    });
  }

  ngOnInit(): void {
    if (this.order.tenantIds?.length) {
      this.tenantsSubscription = this.tenantManagementService
        .getTenants$()
        .pipe(first())
        .subscribe((allTenants) => {
          this.tenants = allTenants.filter((t) =>
            this.order.tenantIds!.includes(t.id)
          );
        });
      this.tenantManagementService.refreshTenants();
    }

    const now = Date.now();

    if (this.order.dueDate) {
      const due = new Date(this.order.dueDate).getTime();
      this.dueDateIsOverdue =
        this.order.orderType !== OrderType.BRINGUP_DONE && due < now;
    }

    if (this.order.startDate && this.order.dueDate) {
      const start = new Date(this.order.startDate).getTime();
      const due = new Date(this.order.dueDate).getTime();
      const totalDuration = due - start;
      const elapsed = now - start;

      if (totalDuration > 0) {
        const progress = (elapsed / totalDuration) * 100;
        this.orderProgress = Math.min(Math.max(progress, 0), 100);
      } else {
        this.orderProgress = 0;
      }
    }

    this.bom = this.order.bom ? [...this.order.bom] : [];
  }

  private getNewCreateBomItemForm(): CreateBomItemFormGroup {
    return new FormGroup({
      name: new FormControl<string | null>(null, Validators.required),
      quantity: new FormControl<number | null>(1, [
        Validators.required,
        Validators.min(1),
      ]),
    });
  }

  protected downloadBillOfMaterials(): void {
    const title = this.order.title ?? "order";
    downloadBillOfMaterialsCsv(this.bom, `${title}-bill-of-materials.csv`);
  }

  protected onBomDrop(event: CdkDragDrop<BillOfMaterialsItem[]>): void {
    moveItemInArray(this.bom, event.previousIndex, event.currentIndex);
    this.saveBillOfMaterials();
  }

  protected changeSelectedBomItem(item: BillOfMaterialsItem): void {
    this.selectedBomItemName = item.name;
    this.updateBomItemForm.patchValue({
      name: item.name ?? null,
      quantity: item.quantity ?? null,
      isAvailable: item.isAvailable ?? false,
    });
  }

  protected createBomItem(): void {
    if (this.createBomItemForm.invalid) return;

    const item: BillOfMaterialsItem = {
      name: this.createBomItemForm.value.name ?? undefined,
      quantity: this.createBomItemForm.value.quantity ?? 1,
    };
    this.bom.push(item);

    this.saveBillOfMaterials();
  }

  protected updateBomItem(): void {
    if (this.updateBomItemForm.invalid || !this.selectedBomItemName) return;

    const { name, quantity, isAvailable } = this.updateBomItemForm.value;
    if (name) {
      this.bom = this.bom.map((item) =>
        item.name === this.selectedBomItemName
          ? {
              ...item,
              name,
              quantity: quantity ?? item.quantity,
              isAvailable: isAvailable ?? undefined,
            }
          : item
      );
      this.selectedBomItemName = name;

      this.saveBillOfMaterials();
    }
  }

  private readonly saveBillOfMaterials = (): void => {
    const putOrderDto: PutOrderDto = { ...this.order, bom: this.bom };

    this.updateBomSubscription?.unsubscribe();
    this.updateBomSubscription = this.orderService
      .updateOrder(putOrderDto)
      .subscribe({
        next: () => this.orderService.refreshOrders(),
        error: () => {
          this.simpleMessageService.showSimpleMessage({
            message: "Failed to update Bill of Materials!",
            icon: "fa-solid fa-triangle-exclamation",
            color: "danger",
          });
        },
      });
  };

  ngOnDestroy(): void {
    this.tenantsSubscription?.unsubscribe();
    this.updateBomSubscription?.unsubscribe();
  }
}

type CreateBomItemFormGroup = FormGroup<{
  name: FormControl<string | null>;
  quantity: FormControl<number | null>;
}>;

type UpdateBomItemFormGroup = FormGroup<{
  name: FormControl<string | null>;
  quantity: FormControl<number | null>;
  isAvailable: FormControl<boolean | null>;
}>;
